import { LicenseInfo } from '@mui/x-license';
import { useQuery } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useMemo } from 'react';

import { fetchMUILicense } from '@/services/api/mui-license';

/**
 * The key is provisioned at runtime, so it always lands one request after the
 * first render. Pass `info` to `Unstable_LicenseInfoProvider` — Pro components
 * re-verify on context change — and gate mounting them on `isSettled`, which is
 * what keeps the missing-key watermark from painting.
 */
export const useMuiLicense = () => {
    const { status } = useSession();
    const { data: licenseKey, isFetched } = useQuery({
        queryKey: ['mui_license'],
        queryFn: async () => {
            const key = await fetchMUILicense();
            if (!key) {
                console.warn('MUI license key not available');
                return key;
            }
            LicenseInfo.setLicenseKey(key);
            return key;
        },
        enabled: status === 'authenticated',
        gcTime: Infinity,
        retry: false,
        staleTime: Infinity,
    });

    return useMemo(
        () => ({
            info: { key: licenseKey ?? undefined },
            isSettled: isFetched,
        }),
        [isFetched, licenseKey],
    );
};
